import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RecruiterSidebar from './RecruiterSidebar';
import ApplicationReview from './ApplicationReview';
import { ref, get, update, remove } from 'firebase/database';  // Add remove import
import { database } from '../../../services/firebase';
import AcceptedCandidates from './AcceptedCandidates';
import DatePickerModal from './DatePickerModal';
import TestResults from './TestResults';

const RecruiterDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [recruiterData, setRecruiterData] = useState(null);
  const [activeTab, setActiveTab] = useState('applications');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const data = localStorage.getItem('recruiterData');
    if (!data) {
      navigate('/recruiter/login');
      return;
    }
    setRecruiterData(JSON.parse(data));
    fetchApplications();
  }, [navigate]);

  // Remove the duplicate checkATSScore function from here
  
  const fetchApplications = async () => {
    setLoading(true); // Set loading when fetch starts
    try {
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      
      if (snapshot.exists()) {
        const users = snapshot.val();
        const companyApplications = [];
        
        Object.keys(users).forEach(userId => {
          const user = users[userId];
          if (user.applications) {
            const userApps = Array.isArray(user.applications) 
              ? user.applications 
              : Object.values(user.applications);
            
            userApps.forEach(app => {
              companyApplications.push({
                ...app,
                id: `${userId}_${app.jobId}`, // Add unique ID
                userId: userId,
                userName: user.fullName || 'Anonymous',
                userEmail: user.email || 'No email provided'
              });
            });
          }
        });
        
        setApplications(companyApplications);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false); // Set loading false when fetch ends
    }
  };

  useEffect(() => {
    const data = localStorage.getItem('recruiterData');
    if (!data) {
      navigate('/recruiter/login');
      return;
    }
    setRecruiterData(JSON.parse(data));
    fetchApplications();
  }, [navigate]); // Remove duplicate useEffect

  // Add new state for modal
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);

  // Update handleUpdateStatus
  const handleUpdateStatus = async (updatedApplication) => {
    try {
      if (updatedApplication.status === 'accepted') {
        setSelectedApplication(updatedApplication);
        setShowDatePicker(true);
        return;
      }

      // Rest of your existing update logic for non-accepted status
      const userRef = ref(database, `users/${updatedApplication.userId}/applications`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        const userApplications = snapshot.val();
        const updatedApplications = Array.isArray(userApplications) 
          ? userApplications.map(app => 
              app.jobId === updatedApplication.jobId 
                ? { ...app, status: updatedApplication.status, testDate: updatedApplication.testDate } 
                : app
            )
          : Object.values(userApplications).map(app => 
              app.jobId === updatedApplication.jobId 
                ? { ...app, status: updatedApplication.status, testDate: updatedApplication.testDate } 
                : app
            );

        await update(ref(database, `users/${updatedApplication.userId}`), {
          applications: updatedApplications
        });
        
        // Update local state
        setApplications(prevApplications => 
          prevApplications.map(app => 
            app.id === updatedApplication.id 
              ? { ...app, status: updatedApplication.status, testDate: updatedApplication.testDate } 
              : app
          )
        );
      }
    } catch (error) {
      console.error('Error updating application status:', error);
    }
  };

  // Add new handler for date selection
  const handleDateSelection = async (testDate) => {
    if (selectedApplication) {
      const updatedApplication = {
        ...selectedApplication,
        status: 'accepted',
        testDate: new Date(testDate).toISOString()
      };
      
      // Update the application with the new test date
      const userRef = ref(database, `users/${updatedApplication.userId}/applications`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        const userApplications = snapshot.val();
        const updatedApplications = Array.isArray(userApplications) 
          ? userApplications.map(app => 
              app.jobId === updatedApplication.jobId ? { ...app, status: 'accepted', testDate: updatedApplication.testDate } : app
            )
          : Object.values(userApplications).map(app => 
              app.jobId === updatedApplication.jobId ? { ...app, status: 'accepted', testDate: updatedApplication.testDate } : app
            );

        await update(ref(database, `users/${updatedApplication.userId}`), {
          applications: updatedApplications
        });
        
        // Update local state
        setApplications(prevApplications => 
          prevApplications.map(app => 
            app.id === updatedApplication.id ? { ...app, status: 'accepted', testDate: updatedApplication.testDate } : app
          )
        );
      }
      
      setShowDatePicker(false);
      setSelectedApplication(null);
    }
  };

  const handleScheduleTest = async (application, testDate) => {
    try {
      const userRef = ref(database, `users/${application.userId}/applications`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        const userApplications = snapshot.val();
        const updatedApplications = Array.isArray(userApplications) 
          ? userApplications.map(app => 
              app.jobId === application.jobId ? { ...app, testDate } : app
            )
          : Object.values(userApplications).map(app => 
              app.jobId === application.jobId ? { ...app, testDate } : app
            );

        await update(ref(database, `users/${application.userId}`), {
          applications: updatedApplications
        });
        
        // Update local state
        setApplications(prevApplications => 
          prevApplications.map(app => 
            app.id === application.id ? { ...app, testDate } : app
          )
        );
      }
    } catch (error) {
      console.error('Error scheduling test:', error);
    }
  };

  const handleRemoveApplication = async (applicationId) => {
    try {
      // Get the application details first
      const appToRemove = applications.find(app => app.id === applicationId);
      if (!appToRemove) return;

      // Update status to rejected in Firebase instead of removing
      const userRef = ref(database, `users/${appToRemove.userId}/applications`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        const userApplications = snapshot.val();
        const updatedApplications = Array.isArray(userApplications)
          ? userApplications.map(app => 
              app.jobId === appToRemove.jobId 
                ? { ...app, status: 'rejected', rejectedAt: new Date().toISOString() }
                : app
            )
          : Object.values(userApplications).map(app => 
              app.jobId === appToRemove.jobId 
                ? { ...app, status: 'rejected', rejectedAt: new Date().toISOString() }
                : app
            );

        // Update the applications in Firebase
        await update(ref(database, `users/${appToRemove.userId}`), {
          applications: updatedApplications
        });
      }

      // Remove from recruiter's view only
      setApplications(prevApplications => 
        prevApplications.filter(app => app.id !== applicationId)
      );
    } catch (error) {
      console.error('Error updating application status:', error);
    }
  };

  // In your JSX
  <ApplicationReview 
    applications={applications}
    onUpdateStatus={handleUpdateStatus}
    onScheduleTest={handleScheduleTest}
    onRemoveApplication={handleRemoveApplication}
  />
  return (
    <div className="flex min-h-screen bg-gray-50">
      <RecruiterSidebar activeTab={activeTab} setActiveTab={setActiveTab} />
      
      <main className="flex-1 ml-64 p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
          </h1>
          <p className="text-gray-600 mt-1">
            Manage your {activeTab} and make informed decisions
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <>
            {activeTab === 'applications' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {applications.map(application => (
                  <div key={application.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300">
                    {/* Header Section */}
                    <div className="p-4 border-b border-gray-100">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800 truncate">{application.userName}</h3>
                          <p className="text-sm text-gray-500 truncate">{application.userEmail}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          application.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          application.status === 'accepted' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {application.status}
                        </span>
                      </div>
                    </div>

                    {/* ATS Scores Section */}
                    <div className="p-4 bg-gray-50">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">ATS Analysis</h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white rounded-lg p-3">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-indigo-600">
                              {application.atsScoresDetailed?.skillsBreakdown ?? application.atsScores?.skills ?? 0}%
                            </div>
                            <div className="text-xs text-gray-500 mt-1">Skills Match</div>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-3">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-indigo-600">
                              {application.atsScoresDetailed?.experienceBreakdown ?? application.atsScores?.experience ?? 0}%
                            </div>
                            <div className="text-xs text-gray-500 mt-1">Experience Match</div>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-3">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-indigo-600">
                              {application.atsScoresDetailed?.certificationsBreakdown ?? application.atsScores?.certifications ?? 0}%
                            </div>
                            <div className="text-xs text-gray-500 mt-1">Certifications Match</div>
                          </div>
                        </div>
                      </div>

                      {/* Explanation Section with See More */}
                      <div className="mt-4">
                        <div className="bg-white rounded-lg p-3">
                          <div className="flex justify-between items-center mb-2">
                            <h5 className="text-sm font-medium text-gray-700">Analysis Summary</h5>
                            <button 
                              onClick={() => {
                                const element = document.getElementById(`explanation-${application.id}`);
                                element.style.maxHeight = element.style.maxHeight ? null : element.scrollHeight + "px";
                              }}
                              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              See {application.showFullExplanation ? 'Less' : 'More'}
                            </button>
                          </div>
                          <div 
                            id={`explanation-${application.id}`}
                            className="transition-all duration-300 ease-in-out overflow-hidden"
                            style={{ maxHeight: '100px' }}
                          >
                            <p className="text-sm text-gray-600 mb-3">{application.explanation}</p>
                            
                            <div className="space-y-4">
                              <div>
                                <h6 className="text-xs font-medium text-gray-600 mb-2">Key Strengths</h6>
                                <div className="flex flex-wrap gap-2">
                                  {application.atsExplanation?.strengths?.map((strength, index) => (
                                    <span key={index} className="px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs">
                                      {strength}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              <div>
                                <h6 className="text-xs font-medium text-gray-600 mb-2">Areas for Growth</h6>
                                <div className="flex flex-wrap gap-2">
                                  {application.atsExplanation?.weaknesses?.map((weakness, index) => (
                                    <span key={index} className="px-2 py-1 bg-red-50 text-red-700 rounded-full text-xs">
                                      {weakness}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              <div>
                                <h6 className="text-xs font-medium text-gray-600 mb-2">Recommendations</h6>
                                <ul className="list-disc pl-4 space-y-1 text-xs text-gray-600">
                                  {application.atsExplanation?.recommendations?.map((rec, index) => (
                                    <li key={index}>{rec}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Skills and Experience Section */}
                      <div className="mt-4 space-y-3">
                      
                        
                        <div className="bg-white rounded-lg p-3">
                          <h5 className="text-xs font-medium text-gray-600 mb-2">Relevant Experience</h5>
                          <div className="text-xs text-gray-600">
                            {application.atsAnalysis?.relevantExperience?.map((exp, index) => (
                              <p key={index} className="mb-1">{exp}</p>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Inside the Actions Section of your application card */}
                    <div className="p-4 border-t border-gray-100">
                      <ApplicationReview
                        application={application}
                        onUpdateStatus={handleUpdateStatus}
                        onScheduleTest={handleScheduleTest}
                        onRemoveApplication={handleRemoveApplication}
                      />
                      
                      {/* Add Logical Test Button */}
                      {application.testStatus?.completed && application.testStatus?.passed && !application.logicalTest && (
                        <button
                          onClick={() => navigate(`/logical-test/${application.userId}/${application.jobId}`)}
                          className="mt-3 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Start Logical Test
                        </button>
                      )}
                    
                      {/* Show Logical Test Status if completed */}
                      {application.logicalTest && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">Logical Test Score:</span>
                            <span className="text-sm font-bold text-indigo-600">{application.logicalTest.score}/100</span>
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            Completed on: {new Date(application.logicalTest.completedAt).toLocaleDateString()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : activeTab === 'accepted' ? (
              <AcceptedCandidates
                applications={applications}
                onScheduleTest={handleScheduleTest}
              />
            ) : null}
          </>
        )}
      </main>
      {showDatePicker && (
        <DatePickerModal
          onClose={() => {
            setShowDatePicker(false);
            setSelectedApplication(null);
          }}
          onSubmit={handleDateSelection}
        />
      )}
    </div>
  );
};

export default RecruiterDashboard;